import { useCallback, useEffect, useMemo, useState } from 'react';

import { Column, useTable, useFilters, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import { Flex, Link, Avatar, useDisclosure } from '@chakra-ui/react';

import { MultiSelectOption } from 'components/atoms/AMultiSelect';
import { useGetSectoListsQuery } from 'core/API/sectoApi';
import { useGetUsersQuery } from 'core/API/usersApi';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';

import { filtreAndOrderColumns } from 'shared/table';
import { BASE_SECTO, BDD_COLUMNS, LIST_FIELDS } from './const';
import { ISecto, ISectoViewData } from './types';

type TSectoProps = { toolId: number };

const AvatarCell = (name: string) => {
    return <Flex>{name && <Avatar size="xs" bg="teal.500" color="white" ml={2} name={name} />}</Flex>;
};

const useSectoContainer = ({ toolId }: TSectoProps) => {
    const translate = useTranslation();
    const [selectedCustom, setSelectedCustoms] = useState<Record<string, { name: string; users: MultiSelectOption[] }>>(
        {},
    );

    const [options, setOptions] = useState<MultiSelectOption[]>([]);
    const [selectedRetailer, setSelectedRetailer] = useState<number>(0);
    const [selectedHiddenHeader, setSelectedHiddenHeader] = useState<Record<string, boolean>>({});

    const { data: users, isError: isUserError, isLoading: isUserLoading } = useGetUsersQuery();
    const { data: lists, isError, isFetching: isLoading } = useGetSectoListsQuery({ idTool: toolId, retailerId: selectedRetailer });

    const { isOpen, onOpen, onClose } = useDisclosure();

    const [sectoData, setListsData] = useState<ISecto[]>([]);
    const [sectoDataView, setListsDataView] = useState<ISectoViewData[]>([]);
    const [selectedSecto, setSelectedSectoData] = useState<ISecto>(BASE_SECTO);

    const headersToHide = useMemo(
        () =>
            [
                ...Object.keys(selectedHiddenHeader).map((header) => {
                    if (selectedHiddenHeader[header]) return header;
                    return '';
                }),
                'customs',
            ].filter((header) => header !== ''),
        [selectedHiddenHeader],
    );

    useEffect(() => {
        if (lists && !isLoading && !isError) {
            const formattedData = lists.lists.map((shop) => {
                const newShop = { ...shop } as ISectoViewData;
                if (newShop.customs) {
                    Object.keys(newShop.customs).forEach((custom) => {
                        newShop[BDD_COLUMNS[custom as keyof typeof BDD_COLUMNS]] = {
                            users: newShop?.customs[custom]?.users,
                            name: newShop?.customs[custom]?.name,
                        };
                    });
                }
                return newShop;
            });

            setListsData(lists.lists);
            setListsDataView(formattedData);
        }
    }, [isError, isLoading, lists]);

    useEffect(() => {
        if (!isUserError && !isUserLoading && users) {
            setOptions(
                users.users.map((user) => {
                    return { id: user.id, name: `${user.first_name} ${user.last_name}` };
                }),
            );
        }
    }, [isUserError, isUserLoading, users]);

    const handleOpenEditAddModal = useCallback(
        (refId: number) => {
            const selectedSectoData = sectoData.find((secto) => secto.id === refId);
            setSelectedCustoms({});
            if (selectedSectoData) {
                const newSelectedCustom: Record<string, { name: string; users: MultiSelectOption[] }> = {};
                if (selectedSectoData.customs) {
                    Object.keys(selectedSectoData.customs).forEach((key) => {
                        const custom = selectedSectoData.customs[key];
                        newSelectedCustom[key] = { ...custom };
                    });
                    setSelectedCustoms(newSelectedCustom);
                }
                setSelectedSectoData(selectedSectoData);
            }
            onOpen();
        },
        [sectoData, selectedSecto],
    );

    const columns: Readonly<Column<any>[]> = useMemo(() => {
        const filteredTableHeader: Column<any>[] = filtreAndOrderColumns(LIST_FIELDS, LIST_FIELDS, headersToHide).map(
            (header: string) => {
                if (header.includes('custom_')) {
                    return {
                        Header: translate(`secto.column.${header}`) as string,
                        accessor: header,
                        Cell: ({ row: { values, original } }) => {
                            return (
                                <Flex>
                                    {values[header]?.name}
                                    {original[header]?.users?.map((manager: any) => AvatarCell(manager.name))}
                                </Flex>
                            );
                        },
                    };
                }
                switch (header) {
                    case 'id':
                        return {
                            Header: translate(`secto.column.${header}`) as string,
                            accessor: header,
                            minWidth: 200,
                            width: 200,
                            Cell: ({ row: { values, original } }) => {
                                return (
                                    <Flex>
                                        <Link
                                            onClick={() => handleOpenEditAddModal(values.id as number)}
                                            color="blue.500"
                                        >
                                            {values.id}
                                        </Link>
                                    </Flex>
                                );
                            },
                        };
                    default:
                        return {
                            Header: translate(`secto.column.${header}`) as string,
                            accessor: header,
                            minWidth: 200,
                            width: 200,
                            Cell: ({ row: { values, original } }) => {
                                return <Flex>{values[header]}</Flex>;
                            },
                        };
                }
            },
        );

        return filteredTableHeader;
    }, [sectoDataView, headersToHide]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [sectoDataView, headersToHide],
    );

    const tableInstance = useTable(
        { data: sectoDataView, columns, defaultColumn },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const props = useMemo(
        () => ({
            isLoading,
            tableInstance,
            selectedSecto,
            selectedCustom,
            options,
            isOpen,
            sectoDataView,
            selectedRetailer,
            selectedHiddenHeader,
            setSelectedHiddenHeader,
            setSelectedRetailer,
            setSelectedSectoData,
            setSelectedCustoms,
            handleOpenEditAddModal,
            onClose,
        }),
        [
            isLoading,
            tableInstance,
            sectoDataView,
            selectedSecto,
            selectedCustom,
            selectedRetailer,
            selectedHiddenHeader,
            options,
            isOpen,
        ],
    );

    return props;
};

export type TSectoViewer = ReturnType<typeof useSectoContainer>;
export default useSectoContainer;
